import { TextField } from "@material-ui/core";
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import userAuthenticationConfig from "../../../../../../utils/userAuthenticationConfig";
import { HTTP_STATUSES, STANDARD_ERRORS_WAY } from "../../../../../../utils/consts";
import { parseAxiosError } from "../../../../../../utils/response";
import CreateBalanceCheckAll from "./CreateBalanceCheckAll";
import CreateBalancesDialogList from "./CreateBalancesDialogList";

import DefaultButton from "../../../../../styles/materialButtonStyle";
import {
  StyledDialogAction,
  StyledDialogContent,
  StyledDialogHead,
  StyledDialogWrapper
} from "../../../../../styles/styledDialog";
import { StyledBalanceList } from "../../../../../services/styledServiceDetails";

const CreateBalancesDialog = ({
  t,
  setOpen,
  notification,
  setNotification,
  serviceId,
  userId
}) => {

  const [disabled, setDisabled] = useState(false);
  const [errors, setErrors] = useState({});
  const [balances, setBalances] = useState([]);
  const [serviceRelations, setServiceRelations] = useState(null);
  const [balanceData, setBalanceData] = useState({});
  const [twofa, setTwofa] = useState("");

  const containsText = (option, searchText) => {
    const text = option.currency.asset;

    return text.toLowerCase().indexOf(searchText.toLowerCase()) > -1;
  };

  const [searchText, setSearchText] = useState("");
  const displayedOptions = useMemo(
    () => {
      if (!serviceRelations) {
        return [];
      }

      return serviceRelations.filter((option) => containsText(option, searchText));
    },
    [searchText, serviceRelations]
  );

  const fetchBalances = () => {
    const filterData = "?service.id=" + serviceId + "&user.id=" + userId + "&pagination=false";

    axios.get("/api/balances" + filterData, userAuthenticationConfig()).then(response => {
      if (response.status === HTTP_STATUSES.HTTP_OK) {
        setBalances(response.data["hydra:member"]);
      }
    }).catch(error => {
      parseAxiosError(eval(STANDARD_ERRORS_WAY), notification, setNotification);
    });
  };

  const fetchServiceRelations = () => {
    const filterData = "?service.id=" + serviceId + "&group_by=currency" + "&pagination=false";

    axios.get("/api/service-relationships" + filterData, userAuthenticationConfig()).then(response => {
      if (response.status === HTTP_STATUSES.HTTP_OK) {
        setServiceRelations(response.data["hydra:member"]);
      }
    }).catch(error => {
      parseAxiosError(eval(STANDARD_ERRORS_WAY), notification, setNotification);
    });
  };

  const createBalances = () => {
    setDisabled(true);

    const data = {
      balances: balanceData,
      twofa: twofa
    };

    axios.post("/api/balances/fill/" + serviceId + "/" + userId, data, userAuthenticationConfig()).then(response => {
      if (response.status === HTTP_STATUSES.HTTP_CREATED) {
        setNotification({
          ...notification,
          visible: true,
          type: "success",
          message: t("details.balancesForm.balancesCreated")
        });

        setOpen(false);
      }
    }).catch(error => {
      parseAxiosError(eval(STANDARD_ERRORS_WAY), notification, setNotification, setErrors);
    });
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleCheckAll = (data) => {
    let checked = data.target.checked;

    if (checked) {
      setBalanceData((prevState) => {
        let balanceDataCopy = { ...prevState };
        serviceRelations.forEach((relation) => {
          balanceDataCopy[relation.currency.asset] = checked;
        });

        return balanceDataCopy;
      });

      return;
    }

    setBalanceData({});
  };

  const updateBalanceData = (data, currency) => {
    let checked = data.target.checked;

    if (checked) {
      setBalanceData((prevState) => { return { ...prevState, [currency]: checked }; });
      return;
    }

    setBalanceData((prevState) => {
      let balanceDataCopy = { ...prevState };
      delete balanceDataCopy[currency];
      return balanceDataCopy;
    });
  };

  useEffect(() => {
    fetchBalances();
    fetchServiceRelations();
  }, []);

  useEffect(() => {
    setDisabled(false);
  }, [notification]);

  useEffect(() => {
    setDisabled(!serviceRelations || !serviceRelations.length || !Object.keys(balanceData).length || Object.keys(errors).length > 0);
  }, [serviceRelations, balanceData, errors]);

  return (
    <>
      <StyledDialogWrapper>
        <StyledDialogHead>
          {t("details.balancesForm.title")}
        </StyledDialogHead>
        <StyledDialogContent>
          <TextField
            size="small"
            autoFocus
            placeholder={t("details.balancesForm.searchPlaceholder")}
            fullWidth
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key !== "Escape") {
                e.stopPropagation();
              }
            }}
          />
          <StyledBalanceList>
            <div className="balances">
              {serviceRelations && !searchText && <CreateBalanceCheckAll
                t={t}
                handleCheckAll={handleCheckAll}
                checked={Object.keys(balanceData).length === serviceRelations.length}
              />}
              <CreateBalancesDialogList
                t={t}
                serviceRelations={serviceRelations}
                displayedOptions={displayedOptions}
                balances={balances}
                balanceData={balanceData}
                updateBalanceData={updateBalanceData}
              />
            </div>
          </StyledBalanceList>
          <TextField
            id="twofa"
            label={t("details.addForm.twoFa")}
            value={twofa}
            onChange={(data) => setTwofa(data.target.value)}
          />
        </StyledDialogContent>
        <StyledDialogAction col="2">
          <DefaultButton
            onClick={handleClose}
            color="danger"
          >
            {t("details.addForm.cancel")}
          </DefaultButton>
          <DefaultButton
            color="success"
            onClick={createBalances}
            disabled={disabled}
          >
            {t("details.addForm.create")}
          </DefaultButton>
        </StyledDialogAction>
      </StyledDialogWrapper>
    </>
  );
};

export default CreateBalancesDialog;